import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import Toastify from '@theme/toastify';


export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // this.startAnimateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  startAnimateAddToCart() {
    const { addToCartButton } = this.refs;

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }
  }

  stopAnimateAddToCart() {
    const { addToCartButton } = this.refs;

    if (addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.remove("atc-added");
    }
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Handle "Upload Images Later" button using event delegation
    this.addEventListener('click', (e) => {
      const target = /** @type {HTMLElement} */ (e.target);
      if (target.closest('[data-upload-later]')) {
        e.preventDefault();
        this.#handleUploadLater();
      }
    }, { signal });
  }

  #handleUploadLater = () => {
    const form = this.querySelector("form");
    if (!form) return;

    const formId = form.id;

    // Disable all file inputs so they're not required
    const fileInputs = form.querySelectorAll('input[type="file"]');
    fileInputs.forEach(input => {
      input.removeAttribute('required');
      input.setAttribute('disabled', '');
    });

    // Get variant ID - check multiple possible sources
    let variantId = null;

    // Try checked radio button that's associated with this form via form attribute
    // These are outside the form but connected via form="formId"
    if (formId) {
      const checkedRadio = /** @type {HTMLInputElement | null} */ (
        document.querySelector(`input[type="radio"][name="id"][form="${formId}"]:checked`)
      );
      if (checkedRadio?.value) {
        variantId = checkedRadio.value;
      }
    }

    // Try hidden input with name="id" inside the form
    if (!variantId) {
      const hiddenInput = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="hidden"][name="id"]'));
      if (hiddenInput?.value) {
        variantId = hiddenInput.value;
      }
    }

    // Try ref="variantId" input
    if (!variantId) {
      const refInput = /** @type {HTMLInputElement | null} */ (form.querySelector('[ref="variantId"]'));
      if (refInput?.value) {
        variantId = refInput.value;
      }
    }

    // Try checked radio button inside the form
    if (!variantId) {
      const checkedVariant = /** @type {HTMLInputElement | null} */ (form.querySelector('input[type="radio"][name="id"]:checked'));
      if (checkedVariant?.value) {
        variantId = checkedVariant.value;
      }
    }

    // Try to get from the section's variant picker - look for checked radio
    if (!variantId) {
      const section = this.closest('.shopify-section');
      const checkedRadio = /** @type {HTMLInputElement | null} */ (section?.querySelector('input[type="radio"][name="id"]:checked'));
      if (checkedRadio?.value) {
        variantId = checkedRadio.value;
      }
    }

    if (!variantId) {
      console.error('No variant ID found');
      alert('Please select a variant first');
      return;
    }

    // Add to cart without images and redirect to checkout
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          // Error adding to cart
          console.error('Error adding to cart:', response.message);
          alert('Error: ' + response.message);
          return;
        }
        // Success - redirect to checkout
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Error:', error);
        alert('An error occurred. Please try again.');
      });
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled"
      ) === "true"
    )
    return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    if (!this.validateVariantUploads()) {
      const message = 'Add the missing required images to continue';

      Toastify({
        text: message,
        duration: 3000,
        newWindow: true,
        close: false,
        avatar: Theme.toast.error_icon,  
        gravity: "bottom", // `top` or `bottom`
        position: "right", // `left`, `center` or `right`
        stopOnFocus: true, // Prevents dismissing of toast on hover
        style: {
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "#B91C1C",
          color: "#ffffff",
          fontSize: window.innerWidth < 768 ? "15px" : "14px",
          borderRadius: "5px",
          boxShadow: "none",
          padding: '12px 16px 12px 8px',
          lineHeight: "1.2"
        },
        onClick: function(){} // Callback after click
      }).showToast()

      this.#setLiveRegionText(message);

      setTimeout(() => { 
        this.#clearLiveRegionText();
      }, 5000)

      return;
    }

    this.refs.addToCartButtonContainer?.startAnimateAddToCart();

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Redirect straight to checkout instead of showing cart drawer
          window.location.href = '/checkout';
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Validate only the controls inside the variant uploads area.
   * Returns true if all enabled controls are non-empty, else false.
   */
  validateVariantUploads() {
    const root =
      this.closest('.shopify-section')?.querySelector('variant-uploads, variant-upload');

    if (!root) return true;

    // Exclude anything with data-upload-instructions
    const singles = Array.from(
      root.querySelectorAll([
        'input:not([type=radio]):not([type=checkbox]):not([type=button]):not([type=submit]):not([type=reset]):not(:disabled):not([data-upload-instructions])',
        'select:not(:disabled):not([data-upload-instructions])',
        'textarea:not(:disabled):not([data-upload-instructions])',
      ].join(', '))
    );

    for (const el of singles) {
      if (el instanceof HTMLInputElement && el.type === 'file') {
        if (!el.files || el.files.length === 0) return false;
        continue;
      }
      if ((el.value ?? '').trim() === '') return false;
    }

    // Radio/checkbox groups: exclude those with data-upload-instructions as well
    const groupInputs = Array.from(
      root.querySelectorAll(
        'input[type=radio]:not(:disabled):not([data-upload-instructions]), input[type=checkbox]:not(:disabled):not([data-upload-instructions])'
      )
    );
    const groupNames = new Set(groupInputs.map(i => i.name).filter(Boolean));

    for (const name of groupNames) {
      const group = root.querySelectorAll(
        `input[name="${CSS.escape(name)}"]:not(:disabled):not([data-upload-instructions])`
      );
      const anyChecked = Array.from(group).some(i => i instanceof HTMLInputElement && i.checked);
      if (!anyChecked) return false;
    }

    return true;
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;

    if (!currentAddToCartButton) return;

    const newPrice = event.detail.resource.price;
    const newPriceWithoutCurrency = Number(newPrice) * 0.01;

    const addToCartButtonPrice = currentAddToCartButton.querySelector(
      ".add-to-cart-text__price"
    );
    if (addToCartButtonPrice) {
      addToCartButtonPrice.innerHTML =
        "- " + Theme.currency + newPriceWithoutCurrency.toFixed(2);
    }

    const addToCartText = addToCartButtonContainer.querySelector('.add-to-cart-text__content');

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      if (addToCartText && addToCartText instanceof HTMLElement) {
        addToCartText.textContent = Theme.translations.sold_out;
      }
        
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true"
      );
    } else {
      addToCartButtonContainer.enable();

      if (addToCartText && addToCartText instanceof HTMLElement) {
        addToCartText.textContent = Theme.translations.add_to_cart;
      }
      
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
